import styles from "./LinkModal.module.css";
import { createPortal } from "react-dom";
import { useState, useEffect } from "react";
import clsx from "clsx";
import { getFaviconUrl } from "../../../utils/common/favicon/faviconUtils";

interface LinkItemProps {
  url: string;
  name: string | undefined;
  index: number;
  visibleTooltipIndex: number | null;
  isSidebarModalOn: number;
  linkMoreModalRef: React.RefObject<HTMLDivElement>;
  onMouseEnter: (index: number) => (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave: () => void;
  onMove: (url: string | undefined) => () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onMoreClick: (index: number) => void;
}

export const LinkItem = ({
  url,
  name,
  index,
  visibleTooltipIndex,
  isSidebarModalOn,
  linkMoreModalRef,
  onMouseEnter,
  onMouseLeave,
  onMove,
  onEdit,
  onDelete,
  onMoreClick,
}: LinkItemProps) => {
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(
    null
  );

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const menuWidth = 100; // modal width from CSS
    const menuHeight = 60; // approximate modal height

    let x = e.clientX;
    let y = e.clientY;

    // 화면 경계 체크 및 조정
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - 10;
    }
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 10;
    }

    setContextMenuPosition({ x, y });
    onMoreClick(index);
  };

  const handleLeftClick = () => {
    onMove(url)();
  };

  // 외부 클릭 감지를 위한 effect
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isSidebarModalOn === index &&
        linkMoreModalRef.current &&
        !linkMoreModalRef.current.contains(event.target as Node)
      ) {
        onMoreClick(-1); // 메뉴 닫기
      }
    };

    if (isSidebarModalOn === index) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isSidebarModalOn, index, linkMoreModalRef, onMoreClick]);

  if (!url) return null;

  const faviconUrl = getFaviconUrl(url);

  return (
    <div
      key={index}
      className={clsx(
        "flex h-[30px] w-max max-w-[300px] cursor-pointer items-center justify-center gap-1.5 overflow-visible rounded-[5px] p-1.5 hover:bg-w-16",
        {
          "bg-w-8-hover": isSidebarModalOn !== index,
          "bg-w-16": isSidebarModalOn === index,
        }
      )}
      onMouseEnter={onMouseEnter(index)}
      onMouseLeave={onMouseLeave}
      onContextMenu={handleContextMenu}
      onClick={handleLeftClick}
    >
      <img
        src={faviconUrl}
        alt="favicon"
        className="h-4 w-4 flex-shrink-0 items-center rounded-[5px]"
      />

      <div className={styles.resource_name}>
        <span>{name}</span>
      </div>

      {isSidebarModalOn === index &&
        createPortal(
          <div
            ref={linkMoreModalRef}
            className={styles["modal"]}
            style={{
              position: "fixed",
              top: `${contextMenuPosition?.y || 0}px`,
              left: `${contextMenuPosition?.x || 0}px`,
              zIndex: 9999,
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(index);
              }}
            >
              Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(index);
              }}
            >
              Delete
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};
